import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import back from 'asset/img/back.png';
import background from 'asset/img/bk-person.webp';
import white from 'asset/img/white.webp';
import { getParentHelpList, parentHelpRead, parentHelpLike } from 'network';
import './index.scss';

// 与后端字段一致：id, title, cover_url, video_url, read_count, like_count, sort_order, user_liked
const FAKE_LIST = [
  {
    id: 1,
    title: '如何培养孩子专注力',
    cover_url: 'https://img.freepik.com/free-photo/child-reading-book_23-2149031702.jpg',
    video_url: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4',
    read_count: 128,
    like_count: 36,
    sort_order: 0,
    user_liked: false,
  },
];

const ParentHelp = () => {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [useFake, setUseFake] = useState(false);
  const fullscreenRef = useRef(null);
  const videoRef = useRef(null);
  const readSentRef = useRef({});

  const fetchList = useCallback(() => {
    setLoading(true);
    getParentHelpList({})
      .then((res) => {
        if (res?.code === 0 && res?.data?.list?.length) {
          setList(res.data.list);
          setUseFake(false);
        } else {
          setList(FAKE_LIST);
          setUseFake(true);
        }
      })
      .catch(() => {
        setList(FAKE_LIST);
        setUseFake(true);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleBack = () => navigate('/personCenter');

  const playVideo = (item) => {
    if (!item?.video_url) {
      message.warning('暂无视频地址');
      return;
    }
    if (!useFake && !readSentRef.current[item.id]) {
      readSentRef.current[item.id] = true;
      parentHelpRead({ video_id: item.id }).then(() => {
        setList((prev) =>
          prev.map((v) => (v.id === item.id ? { ...v, read_count: (v.read_count || 0) + 1 } : v))
        );
      }).catch(() => {});
    }
    setVideoError(false);
    setPlaying(item);
  };

  const [playing, setPlaying] = useState(null);
  const [videoError, setVideoError] = useState(false);

  // 移动端（尤其 iOS）禁止带声自动播放，需在用户点击后显式 play()，失败则静音再播；短延迟保证 video 已挂载并开始加载
  useEffect(() => {
    if (!playing) return;
    const id = setTimeout(() => {
      const el = videoRef.current;
      if (!el) return;
      const p = el.play();
      if (p && typeof p.then === 'function') {
        p.catch(() => {
          el.muted = true;
          el.play().catch(() => {});
        });
      }
    }, 150);
    return () => clearTimeout(id);
  }, [playing]);

  const closeFullscreen = useCallback(() => {
    if (videoRef.current) {
      try {
        videoRef.current.pause();
      } catch (e) {}
    }
    // 仅当当前处于全屏时才调用 exitFullscreen，避免 "Document not active" 报错（如用户已按 ESC 退出或页面失焦）
    const doc = document;
    const inFullscreen = doc.fullscreenElement ?? doc.webkitFullscreenElement ?? doc.mozFullScreenElement ?? doc.msFullscreenElement;
    if (inFullscreen && typeof doc.exitFullscreen === 'function') {
      Promise.resolve(doc.exitFullscreen()).catch(() => {});
    } else if (inFullscreen && typeof doc.webkitExitFullscreen === 'function') {
      Promise.resolve(doc.webkitExitFullscreen()).catch(() => {});
    }
    setVideoError(false);
    setPlaying(null);
  }, []);

  const handleLike = (e, item) => {
    e.stopPropagation();
    if (useFake) {
      setList((prev) =>
        prev.map((v) =>
          v.id === item.id
            ? {
                ...v,
                like_count: v.user_liked ? (v.like_count || 0) - 1 : (v.like_count || 0) + 1,
                user_liked: !v.user_liked,
              }
            : v
        )
      );
      return;
    }
    parentHelpLike({ video_id: item.id })
      .then((res) => {
        if (res?.code === 0 && res?.data) {
          setList((prev) =>
            prev.map((v) =>
              v.id === item.id
                ? {
                    ...v,
                    like_count: res.data.like_count ?? v.like_count,
                    user_liked: !!res.data.user_liked,
                  }
                : v
            )
          );
        }
      })
      .catch(() => message.error('操作失败'));
  };

  return (
    <div className="parenthelp-container">
      <img src={background} className="parenthelp-bg" alt="" />
      <img src={white} className="parenthelp-bg" alt="" />
      <div className="parenthelp-back" onClick={handleBack}>
        <img src={back} alt="返回" />
      </div>
      <div className="parenthelp-header">
        <h1 className="parenthelp-title">育儿顾问</h1>
        <p className="parenthelp-sub">视频解答，陪伴成长</p>
      </div>
      <div className="parenthelp-content">
        {loading ? (
          <div className="parenthelp-loading">加载中…</div>
        ) : (
          <div className="parenthelp-grid">
            {list.map((item) => (
              <div
                key={item.id}
                className="parenthelp-card"
                onClick={() => playVideo(item)}
              >
                <div className="parenthelp-card-cover">
                  <img
                    src={item.cover_url || ''}
                    alt={item.title || ''}
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/400x300?text=封面';
                    }}
                  />
                  <div className="parenthelp-card-play" aria-hidden />
                </div>
                <div className="parenthelp-card-footer">
                  <span className="parenthelp-card-read">
                    <span className="parenthelp-card-icon" aria-hidden>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    </span>
                    <span>{item.read_count ?? 0}</span>
                  </span>
                  <button
                    type="button"
                    className={`parenthelp-card-like ${item.user_liked ? 'is-liked' : ''}`}
                    onClick={(e) => handleLike(e, item)}
                  >
                    <span className="parenthelp-card-icon" aria-hidden>
                      <svg viewBox="0 0 24 24" fill={item.user_liked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
                    </span>
                    <span>{item.user_liked ? '已赞' : '赞'} {item.like_count ?? 0}</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {playing && (
        <div
          className="parenthelp-fullscreen"
          ref={fullscreenRef}
          onClick={closeFullscreen}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Escape' && closeFullscreen()}
        >
          <video
            ref={videoRef}
            src={playing.video_url}
            controls
            autoPlay
            playsInline
            onClick={(e) => e.stopPropagation()}
            onEnded={closeFullscreen}
            onError={() => setVideoError(true)}
          />
          {videoError && (
            <div className="parenthelp-fullscreen-error" onClick={(e) => e.stopPropagation()}>
              <p className="parenthelp-fullscreen-error-msg">视频加载失败</p>
              <p className="parenthelp-fullscreen-error-hint">请检查网络或稍后再试，也可联系管理员确认视频地址是否有效</p>
              <button type="button" className="parenthelp-fullscreen-error-close" onClick={closeFullscreen}>
                关闭
              </button>
            </div>
          )}
          {!videoError && (
            <div className="parenthelp-fullscreen-close" onClick={closeFullscreen}>
              关闭
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ParentHelp;
